import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  EndpointStatus,
  EndpointTestResult,
  LATENCY_THRESHOLD_SLOW,
  LATENCY_THRESHOLD_SUCCESS,
  NetworkInfo,
} from '../diagnostics.models';
import { DIAGNOSTICS_CONFIG } from '../diagnostics.config';
import { DiagnosticsEndpoint } from '@bindoc-projects/app-config-domain-lib';

@Injectable({
  providedIn: 'root',
})
export class NetworkTestService {
  private readonly _config = inject(DIAGNOSTICS_CONFIG, { optional: true });

  private _endpoints$ = new BehaviorSubject<EndpointTestResult[]>([]);
  private _testing$ = new BehaviorSubject<boolean>(false);

  public endpoints$: Observable<EndpointTestResult[]> =
    this._endpoints$.asObservable();
  public testing$: Observable<boolean> = this._testing$.asObservable();

  public getNetworkInfo(): NetworkInfo {
    return {
      online: navigator.onLine,
      endpoints: this._endpoints$.value,
    };
  }

  public initializeEndpoints(): void {
    if (!this._config) {
      this._endpoints$.next([]);
      return;
    }

    const endpoints: EndpointTestResult[] = this._config.endpoints.map(
      (ep: DiagnosticsEndpoint) => ({
        name: ep.name,
        url: ep.healthCheckUrl,
        origin: ep.origin,
        docPath: ep.docPath,
        endpointKeys: ep.endpointKeys,
        status: 'pending' as EndpointStatus,
        description: ep.description,
      }),
    );
    this._endpoints$.next(endpoints);
  }

  public async runAllTests(): Promise<void> {
    if (!this._config || this._testing$.value) {
      return;
    }

    this._testing$.next(true);

    const endpoints = this._endpoints$.value;
    const promises = endpoints.map((_ep, index) => this._testEndpoint(index));
    await Promise.all(promises);

    this._testing$.next(false);
  }

  public async testEndpoint(index: number): Promise<void> {
    await this._testEndpoint(index);
  }

  private async _testEndpoint(index: number): Promise<void> {
    const endpoint = this._endpoints$.value[index];
    if (!endpoint) {
      return;
    }

    const startTime = performance.now();

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        LATENCY_THRESHOLD_SLOW,
      );

      const response = await fetch(endpoint.url, {
        method: 'GET',
        signal: controller.signal,
        mode: 'no-cors',
        credentials: 'omit',
      });

      clearTimeout(timeoutId);

      const latencyMs = Math.round(performance.now() - startTime);
      const isOpaque = response.type === 'opaque';

      let status: EndpointStatus;
      if (!response.ok && !isOpaque) {
        status = 'error';
      } else if (latencyMs < LATENCY_THRESHOLD_SUCCESS) {
        status = 'success';
      } else if (latencyMs < LATENCY_THRESHOLD_SLOW) {
        status = 'slow';
      } else {
        status = 'timeout';
      }

      // Read CURRENT state after async work completes
      const current = [...this._endpoints$.value];
      current[index] = {
        ...endpoint,
        status,
        latencyMs,
        errorMessage:
          !response.ok && !isOpaque ? `HTTP ${response.status}` : undefined,
      };
      this._endpoints$.next(current);
    } catch (error) {
      const latencyMs = Math.round(performance.now() - startTime);
      const isTimeout =
        error instanceof DOMException && error.name === 'AbortError';

      // Read CURRENT state after async work completes
      const current = [...this._endpoints$.value];
      current[index] = {
        ...endpoint,
        status: isTimeout ? 'timeout' : 'error',
        latencyMs,
        errorMessage: isTimeout
          ? 'Request timed out'
          : error instanceof Error
            ? error.message
            : 'Unknown error',
      };
      this._endpoints$.next(current);
    }
  }
}
